import React from 'react';
import {
  Grid,
  GridRow,
  GridColumn,
  Gutter,
  Banner,
  Paragraph,
} from '@axiom/ui';
import type { UseOnboardingTextReturn } from '@axiom/ui';
import type {
  Candidate,
  Opportunity,
  OpportunityOnboarding,
} from '@axiom/validation';

import { OnboardingCandidate } from './OnboardingCandidate';
import { OnboardingFirstDay } from './OnboardingFirstDay';
import { OnboardingCompBenefits } from './OnboardingCompBenefits';
import { OnboardingWorkSchedule } from './OnboardingWorkSchedule';
import { OnboardingLegal } from './OnboardingLegal';
import { OnboardingLegalResources } from './OnboardingLegalResources';

export type OnboardingDetailsProps = {
  name: string;
  position: NonNullable<OpportunityOnboarding['positions']>[number];
  opportunityId: Opportunity['id'];
  formatEngagementAddress: UseOnboardingTextReturn['formatEngagementAddress'];
  texts: ReturnType<UseOnboardingTextReturn['getOnboardingTexts']>;
  onSave: (options: {
    candidateId: Candidate['id'];
    callbackFn: () => void;
  }) => Promise<void>;
};

export const OnboardingDetails = ({
  name,
  position,
  opportunityId,
  formatEngagementAddress,
  texts,
  onSave,
}: OnboardingDetailsProps) => {
  return (
    <Gutter top="16px">
      <Grid stackable name={name} stretched>
        <GridRow>
          <GridColumn largeScreenWidth={4} smallScreenWidth={12}>
            <OnboardingCandidate
              name="ONBOARDING_CARD"
              position={position}
              formatEngagementAddress={formatEngagementAddress}
              onSave={onSave}
            />
            <Gutter top="16px" />
          </GridColumn>
          <GridColumn largeScreenWidth={8} smallScreenWidth={12}>
            {texts?.warnings && texts.warnings.length > 0 ? (
              <Gutter bottom="24px">
                <Banner impact="high" type="warning" name="WARNINGS_BANNER">
                  {texts.warnings.map(txt => (
                    <Gutter bottom="16px" key={txt}>
                      <Paragraph>{txt}</Paragraph>
                    </Gutter>
                  ))}
                </Banner>
              </Gutter>
            ) : null}
            <OnboardingFirstDay
              name="ONBOARDING_FIRST_DAY"
              position={position}
              opportunityId={opportunityId}
              firstDayText={{
                setUpSuccess: texts?.setUpSuccess ?? null,
                startedWithAxiom: texts?.startedWithAxiom ?? null,
              }}
            />
            <Gutter bottom="16px" />
            <OnboardingCompBenefits
              name="ONBOARDING_COMP_BENEFITS"
              position={position}
              opportunityId={opportunityId}
              benefitsText={{
                benefits: texts?.benefits ?? null,
                holiday: texts?.holiday ?? null,
              }}
            />
            <Gutter bottom="16px" />
            <OnboardingWorkSchedule
              name="ONBOARDING_WORK_SCHEDULE"
              scheduleText={{
                weeklyCommitment: texts?.weeklyCommitment ?? null,
                overtime: texts?.overtime ?? null,
              }}
            />
            <Gutter bottom="16px" />
            {texts?.resources ? (
              <>
                <OnboardingLegalResources
                  name="RESOURCES"
                  resourcesText={texts.resources}
                />
                <Gutter bottom="16px" />
              </>
            ) : null}
            <OnboardingLegal
              name="ONBOARDING_LEGAL"
              legalText={{
                describingAxiom: texts?.describingAxiom ?? null,
                supportResources: texts?.supportResources ?? null,
                communications: texts?.communications ?? null,
                workLocationWorkingRemotely:
                  texts?.workLocationWorkingRemotely ?? null,
              }}
            />
            <Gutter bottom="16px" />
          </GridColumn>
        </GridRow>
      </Grid>
    </Gutter>
  );
};
